import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billing, channelWa, inbox, templates as templatesApi, isApiError } from '@repletoryreach/sdk-api';
import type { ComposeRecipientResult, RecipientWindow } from '@repletoryreach/sdk-api/inbox';
import { Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, BreadcrumbTrail, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { useCredits } from '../../../lib/credits';
import { parseComponents, extractVariables } from '../../../lib/template-components';
import { RecipientPicker, type Recipient } from './components/RecipientPicker';
import { WindowStates } from './components/WindowStates';
import { ComposeMessage, templateComponents } from './components/ComposeMessage';
import { SendResults } from './components/SendResults';
import './compose.css';

/**
 * Compose: message this person, or these three.
 *
 * <p>A broadcast names its audience by tag or segment and goes to everybody in
 * it. This names people one at a time. It lives under the inbox because a
 * composed message becomes a conversation, and the inbox is where the reply
 * arrives and where somebody already goes to talk to a person.</p>
 *
 * <p>The rule that shapes the screen is WhatsApp's: a business may send
 * free-form text only within 24 hours of the person's last message to it, and
 * outside that only an approved template. So the screen says, per person and
 * before anything is typed, which side of that line they are on; a selection
 * that straddles it needs a template, because one message goes to all of
 * them. The server enforces the same rule
 * (`POST /v1/conversations/compose`, `WINDOW_CLOSED`): what is shown here is
 * the same reading, not a second opinion.</p>
 *
 * <p>A page rather than a modal on purpose: the recipient list, the per-person
 * window states and the message all have to be visible at once while the
 * selection is being made.</p>
 */
export default function ComposePage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const credits = useCredits();
  const [params] = useSearchParams();

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [numberId, setNumberId] = useState('');
  const [body, setBody] = useState('');
  const [templateCode, setTemplateCode] = useState('');
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<ComposeRecipientResult[] | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  // ── who was named on the way in ────────────────────────────────────────
  //
  // Contacts links here with `?to=+27821110001,+27821110002`, so "Message" on
  // a contact row or on a multi-select lands with the people already chosen.
  useEffect(() => {
    const to = params.get('to');
    if (!to) return;
    const picked = to.split(',').map((m) => m.trim()).filter(Boolean).slice(0, inbox.COMPOSE_MAX_RECIPIENTS);
    if (picked.length > 0) setRecipients(picked.map((msisdn) => ({ msisdn })));
    // Read once, on arrival: re-running would fight the picker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── the numbers it can come from ───────────────────────────────────────
  const numbersQ = useQuery({
    queryKey: ['numbers'],
    queryFn: () => channelWa.listPhoneNumbers(),
    staleTime: 60_000
  });
  const numbers = useMemo(
    () => (numbersQ.data?.data ?? []).filter((n) => n.is_active),
    [numbersQ.data]
  );
  useEffect(() => {
    if (!numberId && numbers.length > 0) setNumberId(numbers[0].id);
  }, [numbers, numberId]);

  // ── where each recipient stands ────────────────────────────────────────
  const msisdns = useMemo(() => recipients.map((r) => r.msisdn), [recipients]);
  const windowQ = useQuery({
    enabled: msisdns.length > 0 && !!numberId,
    queryKey: ['compose', 'window', numberId, msisdns.join(',')],
    queryFn: () => inbox.conversationWindow(msisdns, numberId),
    placeholderData: (prev) => prev,
    staleTime: 30_000
  });
  const windowRows: RecipientWindow[] = windowQ.data?.data ?? [];
  const windowMeta = windowQ.data?.meta ?? null;

  /*
   * Which kind of message this has to be.
   *
   * Not a control the person picks: it is the consequence of who they chose.
   * Everybody inside the window means free text is allowed; anybody outside it
   * means a template, because one message goes to all of them. Forced to
   * `template` rather than merely warned about, so the screen cannot show a
   * text box for a send the server will refuse.
   */
  const mode: 'text' | 'template' =
    windowRows.length > 0 && windowMeta?.free_text_allowed ? 'text' : 'template';

  // ── templates ──────────────────────────────────────────────────────────
  const templatesQ = useQuery({
    queryKey: ['templates'],
    queryFn: () => templatesApi.list(),
    staleTime: 60_000
  });
  // `dev_fallback` templates were auto-approved outside production and Meta
  // has never seen them: they are not sendable, the way the wizard treats them.
  const sendable = useMemo(
    () => (templatesQ.data?.data ?? []).filter((tpl) => tpl.status === 'approved' && !tpl.dev_fallback),
    [templatesQ.data]
  );
  const blockedByDevFallback = useMemo(
    () => (templatesQ.data?.data ?? []).filter((tpl) => tpl.status === 'approved' && tpl.dev_fallback).length,
    [templatesQ.data]
  );
  const template = useMemo(
    () => sendable.find((tpl) => tpl.code === templateCode) ?? null,
    [sendable, templateCode]
  );
  const parsed = useMemo(() => parseComponents(template?.components), [template?.components]);
  const vars = useMemo(() => extractVariables(parsed).map(String), [parsed]);

  // ── what it will cost, and what is in the wallet ───────────────────────
  //
  // The same price book and country rule the meter applies afterwards, so the
  // figure quoted here is the figure that lands on the ledger.
  const estimateQ = useQuery({
    enabled: msisdns.length > 0,
    queryKey: ['compose', 'estimate', mode, template?.category ?? 'service', msisdns.join(',')],
    queryFn: () => billing.estimateUsage({
      category: mode === 'template' ? (template?.category ?? 'utility') : 'service',
      recipients: msisdns
    }),
    placeholderData: (prev) => prev,
    retry: false,
    staleTime: 30_000
  });
  const estimate = estimateQ.data?.data ?? null;

  const reachable = windowRows.filter((r) => r.opted_in);
  const optedOut = windowRows.length - reachable.length;

  const canSend =
    !sending
    && recipients.length > 0
    && !!numberId
    && reachable.length > 0
    && (mode === 'text' ? body.trim().length > 0 : !!template)
    && estimate?.ok !== false;

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (mode === 'text' && body.trim().length === 0) next.body = t('compose.error.bodyRequired');
    if (mode === 'template') {
      if (!template) next.templateCode = t('compose.error.templateRequired');
      else for (const v of vars) {
        if (!(variables[v] ?? '').trim()) next[`var-${v}`] = t('compose.error.placeholderRequired');
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function send(only?: string[]) {
    if (!validate()) return;
    const to = only ?? msisdns;
    if (to.length === 0) return;

    setSending(true);
    setRefusal(null);
    try {
      const response = await inbox.compose({
        number_id: numberId,
        recipients: to,
        type: mode,
        ...(mode === 'text'
          ? { text: { body: body.trim() } }
          : {
            template: {
              name: template!.code,
              language: template!.language,
              category: template!.category ?? undefined,
              components: templateComponents(parsed, variables)
            }
          })
      });

      const fresh = response.data.results;
      // A retry replaces only the rows it was about, so the people reached on
      // the first attempt stay reported as reached.
      setResults((prev) => {
        if (!prev) return fresh;
        const byMsisdn = new Map(fresh.map((r) => [r.msisdn, r]));
        return prev.map((r) => byMsisdn.get(r.msisdn) ?? r);
      });

      qc.invalidateQueries({ queryKey: ['conversations'] });
      qc.invalidateQueries({ queryKey: ['compose', 'window'] });
      qc.invalidateQueries({ queryKey: ['wallet'] });

      const sentRows = fresh.filter((r) => r.status === 'sent');
      if (response.meta.failed === 0) {
        notifySuccess(t('compose.toast.sent', { count: response.meta.sent }), t('compose.title'));
        // One person, one conversation: go straight to it. Several, and the
        // summary above is the useful place to be.
        if (sentRows.length === 1 && to.length === 1 && sentRows[0].conversation_id) {
          nav(`/inbox?conversation=${encodeURIComponent(sentRows[0].conversation_id)}`);
          return;
        }
      } else if (response.meta.sent > 0) {
        notifyError(
          t('compose.toast.partial', { sent: response.meta.sent, failed: response.meta.failed }),
          t('compose.title')
        );
      } else {
        notifyError(t('compose.toast.noneSent'), t('compose.title'));
      }
    } catch (e) {
      // A refusal of the WHOLE send: it never reached anybody, so it is said
      // in place rather than as a toast that scrolls away.
      const closed = inbox.windowClosedRecipients(e);
      if (closed) {
        setRefusal(t('compose.refusal.windowClosed'));
      } else if (isApiError(e)) {
        const known = t(`compose.refusal.${e.code}`, '');
        setRefusal(known || e.message || t('compose.refusal.unknown'));
      } else {
        setRefusal(e instanceof Error ? e.message : t('compose.refusal.unknown'));
      }
    } finally {
      setSending(false);
    }
  }

  function startAgain() {
    setResults(null);
    setRecipients([]);
    setBody('');
    setTemplateCode('');
    setVariables({});
    setErrors({});
    setRefusal(null);
  }

  return (
    <Stack gap="lg" className="rr-compose">
      <PageHeader
        title={t('compose.title')}
        description={t('compose.description')}
        withDivider={false}
        breadcrumbs={
          <BreadcrumbTrail
            items={[{ label: t('nav.inbox'), href: '/inbox' }, { label: t('compose.title') }]}
            renderLink={(href, label) => <Link to={href}>{label}</Link>}
          />
        }
        actions={
          <Group gap="xs">
          <HelpButton guide="audience" />
          <Button size="sm" variant="default" onClick={() => nav('/inbox')}>
            {t('action.cancel')}
          </Button>
          </Group>
        }
      />

      {numbersQ.isError && (
        <Alert color="error" role="alert">{t('compose.error.numbersFailed')}</Alert>
      )}
      {!numbersQ.isPending && numbers.length === 0 && (
        <Alert color="warning" title={t('compose.error.noNumbers.title')}>
          <Stack gap={6}>
            <Text role="bodySmall">{t('compose.error.noNumbers.body')}</Text>
            <Button size="xs" variant="light" onClick={() => nav('/numbers')}>
              {t('compose.error.noNumbers.action')}
            </Button>
          </Stack>
        </Alert>
      )}

      {results ? (
        <SendResults
          results={results}
          sending={sending}
          onRetry={(only) => send(only)}
          onNew={startAgain}
          metaPayer={numbers.find((n) => n.id === numberId)?.meta_billing_payer}
        />
      ) : (
        <div className="rr-compose__grid">
          <Stack gap="lg" className="rr-compose__main">
            <Card withBorder data-guide="compose-who">
              <Stack gap="md">
                <Text role="title">{t('compose.step.who')}</Text>

                {numbers.length > 1 && (
                  <Select
                    label={t('compose.number.label')}
                    description={t('compose.number.hint')}
                    value={numberId}
                    onChange={(e) => setNumberId(e.currentTarget.value)}
                    // Never a raw identifier as a label (UI-CONVENTIONS §1): a
                    // number attached before its details came back from Meta
                    // has neither name nor msisdn, and its guid means nothing
                    // to the person choosing.
                    data={numbers.map((n) => ({
                      value: n.id,
                      label: n.display_name && n.msisdn
                        ? `${n.display_name} · ${n.msisdn}`
                        : (n.display_name ?? n.msisdn ?? t('compose.number.unnamed'))
                    }))}
                  />
                )}

                <RecipientPicker
                  value={recipients}
                  onChange={setRecipients}
                  max={inbox.COMPOSE_MAX_RECIPIENTS}
                  disabled={sending}
                />

                <WindowStates
                  rows={windowRows}
                  meta={windowMeta}
                  loading={windowQ.isFetching}
                  templateMode={mode === 'template'}
                  templateChosen={!!template}
                />

                {optedOut > 0 && (
                  <Alert color="warning">{t('compose.window.optedOut', { count: optedOut })}</Alert>
                )}
              </Stack>
            </Card>

            <Card withBorder data-guide="compose-what">
              <Stack gap="md">
                <Text role="title">{t('compose.step.what')}</Text>
                <ComposeMessage
                  mode={mode}
                  body={body}
                  onBody={setBody}
                  templateCode={templateCode}
                  onTemplateCode={setTemplateCode}
                  variables={variables}
                  onVariables={setVariables}
                  sendable={sendable}
                  templatesLoading={templatesQ.isPending}
                  blockedByDevFallback={blockedByDevFallback}
                  errors={errors}
                  disabled={sending}
                />
              </Stack>
            </Card>
          </Stack>

          <div className="rr-compose__aside">
            <Card withBorder className="rr-compose__summary" data-guide="compose-send">
              <Stack gap="md">
                <Text role="title">{t('compose.step.send')}</Text>

                <Stack gap={4}>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('compose.summary.recipients')}</Text>
                  <Text role="headline">{reachable.length}</Text>
                  {optedOut > 0 && (
                    <Text role="caption" c="var(--rr-color-text-muted)">
                      {t('compose.summary.excluded', { count: optedOut })}
                    </Text>
                  )}
                </Stack>

                <Stack gap={4}>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('compose.summary.cost')}</Text>
                  <Text role="headline">
                    {estimate ? credits(estimate.total_minor) : estimateQ.isFetching ? '…' : '–'}
                  </Text>
                </Stack>

                <Stack gap={4}>
                  <Text role="caption" c="var(--rr-color-text-muted)">{t('compose.summary.balance')}</Text>
                  <Text role="headline">{estimate ? credits(estimate.balance_minor) : '–'}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {estimate
                      ? t('compose.summary.balanceAfter', {
                        after: credits(Math.max(0, estimate.balance_minor - estimate.total_minor))
                      })
                      : t('compose.summary.balanceUnknown')}
                  </Text>
                </Stack>

                {/* The server's own verdict on whether the wallet covers it,
                    not a sum done in the browser. */}
                {estimate?.ok === false && (
                  <Alert color="error" role="alert" title={t('compose.summary.short.title')}>
                    <Stack gap={6}>
                      <Text role="bodySmall">
                        {t('compose.summary.short.body', { shortfall: credits(estimate.shortfall_minor) })}
                      </Text>
                      <Button size="xs" variant="light" onClick={() => nav('/billing')}>
                        {t('compose.summary.short.action')}
                      </Button>
                    </Stack>
                  </Alert>
                )}

                {refusal && <Alert color="error" role="alert">{refusal}</Alert>}

                <Button fullWidth loading={sending} disabled={!canSend} onClick={() => send()}>
                  {mode === 'text'
                    ? t('compose.action.send', { count: reachable.length })
                    : t('compose.action.sendTemplate', { count: reachable.length })}
                </Button>

                <Group gap={4} align="center" wrap="wrap">
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('compose.summary.broadcastHint')}
                  </Text>
                  <Button size="xs" variant="subtle" onClick={() => nav('/broadcasts/new')}>
                    {t('compose.summary.broadcastAction')}
                  </Button>
                </Group>
              </Stack>
            </Card>
          </div>
        </div>
      )}
    </Stack>
  );
}
